import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  normalizeDropdownLibrary,
  createDropdownDefinition,
  createDropdownOption,
} from "./dropdownLibraryUtils";

const getDialogTitle = (section) => {
  if (!section) {
    return "Add dropdown";
  }
  const title = section.title || "Untitled section";
  return `Add dropdown · ${title}`;
};

const DropdownLibraryDialog = ({ open, section, onClose, onChange }) => {
  const sectionId = section?.id || null;

  const existingDropdowns = useMemo(
    () => normalizeDropdownLibrary(section?.metadata?.dropdowns),
    [section?.metadata?.dropdowns]
  );

  const [dropdownForm, setDropdownForm] = useState(() =>
    createDropdownDefinition(existingDropdowns.length)
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setDropdownForm(createDropdownDefinition(existingDropdowns.length));
  }, [existingDropdowns.length, open]);

  const handleFieldChange = useCallback((updates) => {
    setDropdownForm((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleOptionChange = useCallback((optionIndex, updates) => {
    setDropdownForm((prev) => {
      if (optionIndex < 0 || optionIndex >= prev.options.length) {
        return prev;
      }

      const nextOptions = prev.options.map((option, idx) =>
        idx === optionIndex ? { ...option, ...updates } : option
      );

      return { ...prev, options: nextOptions };
    });
  }, []);

  const handleAddOption = useCallback(() => {
    setDropdownForm((prev) => ({
      ...prev,
      options: [...prev.options, createDropdownOption(prev.options.length)],
    }));
  }, []);

  const handleRemoveOption = useCallback((optionIndex) => {
    setDropdownForm((prev) => {
      if (optionIndex < 0 || optionIndex >= prev.options.length) {
        return prev;
      }

      const nextOptions = prev.options.filter((_, idx) => idx !== optionIndex);

      return {
        ...prev,
        options: nextOptions.length
          ? nextOptions
          : [createDropdownOption()],
      };
    });
  }, []);

  const handleSubmit = useCallback(() => {
    if (!sectionId || !onChange) {
      return;
    }

    const nextDropdown = {
      ...dropdownForm,
      options: dropdownForm.options.map((option, index) =>
        createDropdownOption(index, option)
      ),
    };

    const nextDropdowns = [...existingDropdowns, nextDropdown];

    onChange(sectionId, nextDropdowns);
    onClose?.();
  }, [dropdownForm, existingDropdowns, onChange, onClose, sectionId]);

  const disableSubmit = !sectionId || !dropdownForm.name?.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="dropdown-library-dialog-title"
    >
      <DialogTitle id="dropdown-library-dialog-title">
        {getDialogTitle(section)}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {!section ? (
            <Typography variant="body2" color="text.secondary">
              Select a section to add a dropdown that can be reused across your
              assignment.
            </Typography>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.default" }}>
              <Stack spacing={1.5}>
                <TextField
                  label="Dropdown name"
                  value={dropdownForm.name}
                  onChange={(event) =>
                    handleFieldChange({ name: event.target.value })
                  }
                  fullWidth
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dropdownForm.allowMultiple}
                      onChange={(event) =>
                        handleFieldChange({ allowMultiple: event.target.checked })
                      }
                    />
                  }
                  label="Allow multiple selections"
                />
                <Stack spacing={1}>
                  {dropdownForm.options.map((option, optionIndex) => (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "stretch", sm: "center" }}
                      key={option.id}
                    >
                      <TextField
                        label={`Option ${optionIndex + 1}`}
                        value={option.label}
                        onChange={(event) =>
                          handleOptionChange(optionIndex, {
                            label: event.target.value,
                            value: event.target.value,
                          })
                        }
                        fullWidth
                      />
                      <IconButton
                        aria-label={`Remove option ${optionIndex + 1}`}
                        onClick={() => handleRemoveOption(optionIndex)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddOption}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Add option
                </Button>
              </Stack>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<AddIcon />}
          disabled={disableSubmit}
        >
          Add dropdown
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DropdownLibraryDialog;
